import { useCallback, useEffect, useRef, useState } from "react";
import { Users, Copy, LogIn, PartyPopper, Map, X, ArrowRight, Gamepad2, Radio } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { IS_INGAME } from "@/components/cef/cefShared";
import { useI18n } from "@/i18n";

const DISCORD_URL = "https://discord.gg/NPhbFVSmpd";

function anonId() {
  let id = localStorage.getItem("nc_anon");
  if (!id) { id = crypto.randomUUID(); localStorage.setItem("nc_anon", id); }
  return id;
}

/* Citizens panel — Steam identity, who's online, parties, invites, Discord. */
export function SocialPanel({ accent = "#2E6BFF", audio, scene }) {
  const [me, setMe] = useState(undefined);
  const [presence, setPresence] = useState({ online: 0, citizens: [], party_members: [] });
  const [party, setParty] = useState(() => localStorage.getItem("nc_party") || "");
  const [joinCode, setJoinCode] = useState("");
  const loggedIn = !!localStorage.getItem("nc_token");

  useEffect(() => {
    if (loggedIn) api.get("/auth/me").then((r) => setMe(r.data)).catch(() => setMe(null));
    else setMe(null);
  }, [loggedIn]);

  const beat = useCallback(() => {
    api.post("/nc/presence/beat", { anon_id: anonId(), party, scene, party_host: localStorage.getItem("nc_party_host") === "1" }).catch(() => {});
    api.get("/nc/presence/online", { params: { party } }).then((r) => setPresence(r.data)).catch(() => {});
  }, [party, scene]);

  useEffect(() => {
    beat();
    const t = setInterval(beat, 20000);
    return () => clearInterval(t);
  }, [beat]);

  const createParty = () => {
    const code = Array.from({ length: 6 }, () => "ABCDEFGHJKMNPQRSTUVWXYZ23456789"[(Math.random() * 31) | 0]).join("");
    localStorage.setItem("nc_party", code);
    localStorage.setItem("nc_party_host", "1"); // creator leads — can move the party
    setParty(code);
    audio.click();
    toast.success(`Party created — share code ${code}`);
  };
  const joinParty = () => {
    const code = joinCode.trim().toUpperCase();
    if (!/^[A-Z0-9]{4,8}$/.test(code)) { audio.deny(); toast.error("Party codes look like KX7R2P"); return; }
    localStorage.setItem("nc_party", code);
    localStorage.setItem("nc_party_host", "0");
    setParty(code); setJoinCode("");
    audio.click(); toast.success(`Joined party ${code}`);
  };
  const leaveParty = () => { localStorage.removeItem("nc_party"); localStorage.removeItem("nc_party_host"); setParty(""); audio.back(); };
  const copy = (text, label) => { navigator.clipboard?.writeText(text).then(() => toast.success(`${label} copied`)).catch(() => {}); };

  return (
    <div data-testid="social-panel" className="flex flex-col gap-4">
      {/* identity */}
      {me ? (
        <div className="flex items-center gap-3 border border-white/10 bg-black/40 p-3">
          {(me.steam_avatar || me.avatar_path) ? (
            <img src={me.steam_avatar || me.avatar_path} alt="" className="w-10 h-10 object-cover border border-white/15" />
          ) : (
            <div className="w-10 h-10 border border-white/15 flex items-center justify-center font-bold text-lg" style={{ color: accent }}>{(me.name || "C")[0].toUpperCase()}</div>
          )}
          <div className="min-w-0">
            <p data-testid="social-me" className="font-semibold text-sm text-zinc-100 truncate">{me.steam_persona || me.name || "Citizen"}</p>
            <p className="font-mono text-[9px] text-zinc-400 uppercase tracking-[0.15em]">{me.steam_persona ? "Steam linked" : "Hub account"}</p>
          </div>
        </div>
      ) : IS_INGAME ? (
        <div className="border border-white/10 bg-black/40 p-4">
          <p className="font-mono text-[10px] text-zinc-400 leading-relaxed">Sign-in lives on the web hub — visit <span className="text-zinc-200">specpixel.com</span> on your desktop and your Steam name & avatar will show here automatically. Links are disabled in-game so you never lose the menu.</p>
        </div>
      ) : (
        <div className="border border-white/10 bg-black/40 p-4 flex flex-col gap-2.5">
          <p className="font-mono text-[10px] text-zinc-300 leading-relaxed">Sign in to show your Steam name & avatar, party up and see your friends here.</p>
          <button data-testid="steam-signin-btn" onClick={() => { window.location.href = `/api/auth/steam/login?next=${encodeURIComponent("/cef/menu")}`; }}
            className="w-full py-2.5 font-bold uppercase tracking-[0.2em] text-xs text-black flex items-center justify-center gap-2 transition-transform hover:scale-[1.02]"
            style={{ backgroundColor: "#66c0f4" }}>
            <LogIn className="w-3.5 h-3.5" /> Sign in with Steam
          </button>
          <a data-testid="hub-signin-link" href="/auth" className="font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-400 hover:text-zinc-100 text-center transition-colors">or use a hub account</a>
        </div>
      )}

      {/* who's online */}
      <div>
        <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-zinc-400 mb-2 flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Citizens online — {presence.online}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {presence.citizens.slice(0, 12).map((c, i) => (
            <span key={i} data-testid={`online-citizen-${i}`} className="flex items-center gap-1.5 border border-white/10 bg-black/40 pl-1 pr-2 py-1">
              {c.avatar ? <img src={c.avatar} alt="" className="w-4 h-4 object-cover" /> : <span className="w-4 h-4 flex items-center justify-center text-[8px] font-bold" style={{ color: accent }}>{c.name[0]}</span>}
              <span className="font-mono text-[9px] text-zinc-300">{c.name}</span>
              <span className="font-mono text-[7px] text-zinc-500 uppercase">{c.scene}</span>
            </span>
          ))}
          {presence.citizens.length === 0 && <p className="font-mono text-[9px] text-zinc-500">No identified citizens in menus right now — be the first.</p>}
        </div>
      </div>

      {/* party */}
      <div className="border border-white/10 bg-black/40 p-3.5 flex flex-col gap-2.5">
        <p className="font-mono text-[9px] tracking-[0.3em] uppercase text-zinc-400 flex items-center gap-1.5"><PartyPopper className="w-3.5 h-3.5" style={{ color: accent }} /> Party up</p>
        {party ? (
          <>
            <div className="flex items-center gap-2">
              <code data-testid="party-code" className="font-mono text-sm tracking-[0.3em] text-zinc-100 border border-white/15 bg-black px-3 py-1.5">{party}</code>
              <button data-testid="party-copy" onClick={() => copy(party, "Party code")} className="border border-white/15 p-2 text-zinc-300 hover:border-white/40 transition-colors"><Copy className="w-3.5 h-3.5" /></button>
              <button data-testid="party-leave" onClick={leaveParty} className="border border-red-500/40 p-2 text-red-400 hover:bg-red-500/10 transition-colors"><X className="w-3.5 h-3.5" /></button>
              {localStorage.getItem("nc_party_host") === "1" && (
                <span data-testid="party-leader-badge" className="font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-1 border" style={{ borderColor: `${accent}66`, color: accent }}>You lead</span>
              )}
            </div>
            <p className="font-mono text-[10px] text-zinc-400 uppercase tracking-[0.1em]">{presence.party_members.length} identified member{presence.party_members.length === 1 ? "" : "s"} in this party</p>
            {localStorage.getItem("nc_party_host") === "1" && (
              <p className="font-mono text-[10px] text-zinc-300 leading-relaxed">As leader you can move the whole party: pick a server in the browser and hit <span style={{ color: accent }}>MOVE PARTY</span>.</p>
            )}
          </>
        ) : (
          <div className="flex gap-2">
            <button data-testid="party-create" onClick={createParty} className="px-3 py-2 font-mono text-[9px] uppercase tracking-[0.2em] text-black" style={{ backgroundColor: accent }}>Create party</button>
            <input data-testid="party-join-input" value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} maxLength={8} placeholder="CODE"
              className="w-24 bg-black border border-white/15 px-2 py-2 font-mono text-[10px] tracking-[0.2em] uppercase focus:outline-none focus:border-white/40" />
            <button data-testid="party-join" onClick={joinParty} className="border border-white/20 px-3 py-2 font-mono text-[9px] uppercase tracking-[0.15em] text-zinc-200 hover:border-white/50 transition-colors">Join</button>
          </div>
        )}
      </div>

      {/* invite + discord */}
      <div className="flex flex-col gap-2">
        <button data-testid="invite-friends" onClick={() => copy(`${window.location.origin}/cef/menu`, "Invite link")}
          className="w-full border border-white/15 hover:border-white/40 py-2.5 font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-200 transition-colors flex items-center justify-center gap-2">
          <Users className="w-3.5 h-3.5" /> Invite friends — copy link
        </button>
        <a data-testid="discord-join" href={IS_INGAME ? undefined : DISCORD_URL} target="_blank" rel="noopener noreferrer"
          onClick={IS_INGAME ? (e) => { e.preventDefault(); toast.info("Discord opens on desktop — discord.gg/NPhbFVSmpd"); } : undefined}
          className={`w-full py-2.5 font-bold uppercase tracking-[0.2em] text-xs text-center text-white transition-colors flex items-center justify-center gap-2 ${IS_INGAME ? "opacity-60 cursor-not-allowed" : ""}`}
          style={{ backgroundColor: "#5865F2" }}>
          <Radio className="w-3.5 h-3.5" /> {IS_INGAME ? "Discord — desktop only" : "Join the official Discord"}
        </a>
      </div>
    </div>
  );
}

/* Roadmap panel — compact, readable, scrollable inside the menu rail. */
export function RoadmapPanel({ accent = "#2E6BFF" }) {
  const [roadmap, setRoadmap] = useState(null);
  useEffect(() => { api.get("/roadmap").then((r) => setRoadmap(r.data)).catch(() => {}); }, []);
  if (!roadmap) return <p className="font-mono text-[11px] text-zinc-400 uppercase tracking-[0.2em]">Loading roadmap…</p>;
  return (
    <div data-testid="roadmap-panel" className="flex flex-col gap-2.5">
      <p className="font-mono text-[10px] text-zinc-300 uppercase tracking-[0.15em] leading-relaxed">{roadmap.motto}</p>
      {(roadmap.phases || []).map((ph) => {
        const items = ph.items || [];
        const done = items.filter((i) => i.status === "done").length;
        const pct = items.length ? Math.round(items.reduce((n, i) => n + (i.progress ?? (i.status === "done" ? 100 : 0)), 0) / items.length) : 0;
        const active = items.find((i) => i.status === "in_progress") || items.find((i) => i.status !== "done");
        return (
          <div key={ph.id} data-testid={`roadmap-${ph.id}`} className="border border-white/10 bg-black/40 px-3.5 py-3">
            <div className="flex items-center justify-between gap-2">
              <p className="font-semibold uppercase tracking-[0.1em] text-[12px] text-zinc-100 leading-tight">{ph.number} · {ph.title}</p>
              <span className="font-mono text-[10px] text-zinc-300 shrink-0">{pct}%</span>
            </div>
            <div className="h-1 bg-white/10 overflow-hidden mt-2"><div className="h-full" style={{ width: `${pct}%`, backgroundColor: accent, boxShadow: `0 0 8px ${accent}` }} /></div>
            {active && <p className="font-mono text-[10px] text-zinc-300 leading-snug mt-2"><span style={{ color: accent }}>NOW:</span> {active.title}</p>}
          </div>
        );
      })}
      {!IS_INGAME && (
        <a data-testid="roadmap-full" href="/new-citizen" target="_blank" rel="noopener noreferrer" className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-200 hover:text-white transition-colors flex items-center gap-1.5 mt-1">
          <Map className="w-3.5 h-3.5" /> Full roadmap <ArrowRight className="w-3 h-3" />
        </a>
      )}
    </div>
  );
}

/* Live ticker — rotating hub/steam/news line above the footer. */
export function LiveTicker({ stats, accent = "#2E6BFF" }) {
  const { t } = useI18n();
  const [i, setI] = useState(0);
  const items = [
    stats ? `${t("title.stats_live", { n: stats.online })} · ${t("title.stats_citizens", { n: stats.players })}` : t("menu.linking"),
    "NEW // in-game admin console — live roster moderation, world controls, bans",
    "NEW // entry exams — server owners can quiz players before they enter",
    "NEW // sign in with Steam — your name & avatar follow you into the menu",
    "Phone banking, police CAD and the courthouse are live in-world",
    "Owners: design your loading screen in the Loading Studio — /admin/hub",
  ];
  useEffect(() => {
    const t = setInterval(() => setI((x) => x + 1), 4200);
    return () => clearInterval(t);
  }, []);
  return (
    <div data-testid="live-ticker" className="flex items-center gap-3 overflow-hidden">
      <span className="w-1.5 h-1.5 rounded-full animate-pulse shrink-0" style={{ backgroundColor: accent }} />
      <p key={i} className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-300 whitespace-nowrap" style={{ animation: "ncFadeUp 0.5s ease both" }}>
        {items[i % items.length]}
      </p>
    </div>
  );
}

/* First-visit guided tour — skippable, never forced. */
export function TourOverlay({ accent = "#2E6BFF", onDone }) {
  const [step, setStep] = useState(0);
  const STEPS = [
    { title: "Welcome to the new New Citizen", body: "The whole front-end of the game was rebuilt: this menu, the live server browser, in-game banking, police CAD, a working courthouse, and proper tools for server owners. This 20-second tour shows you around — or skip it, the city doesn't wait." },
    { title: "Server Browser", body: "SERVER BROWSER lists every live world — official Steam servers AND community hub worlds, with filters for region, population and official-only. QUICK MATCH drops you into the busiest live server instantly. HOST YOUR OWN walks you through renting or self-hosting." },
    { title: "Citizens & parties", body: "On the right: sign in with Steam to carry your real name and avatar, see who's online, create a party code for your friends, and join the official Discord." },
    { title: "Roadmap & city wire", body: "The right rail also tracks the live city — busiest world, citizens in town — and the real development roadmap so you always know what's next." },
    { title: "Make it yours", body: "SETTINGS now has live audio mixing for this menu plus graphics presets. That's the tour — press any key, the city is waiting." },
  ];
  const s = STEPS[step];
  return (
    <div data-testid="tour-overlay" className="absolute inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-6" style={{ animation: "ncFadeIn 0.3s ease both" }}>
      <div className="w-full max-w-lg border bg-[#080a0f]/95 p-6 flex flex-col gap-4" style={{ borderColor: `${accent}55`, boxShadow: `0 0 60px ${accent}22` }}>
        <div className="flex items-center gap-3">
          <Gamepad2 className="w-4 h-4" style={{ color: accent }} />
          <p className="font-mono text-[9px] tracking-[0.35em] uppercase" style={{ color: accent }}>What's new — {step + 1} / {STEPS.length}</p>
          <button data-testid="tour-skip" onClick={onDone} className="ml-auto font-mono text-[9px] uppercase tracking-[0.2em] text-zinc-500 hover:text-zinc-100 transition-colors">Skip tour</button>
        </div>
        <div className="flex gap-1">{STEPS.map((_, j) => <span key={j} className="h-0.5 flex-1" style={{ backgroundColor: j <= step ? accent : "rgba(255,255,255,0.12)" }} />)}</div>
        <p className="font-bold uppercase tracking-[0.15em] text-lg text-zinc-50">{s.title}</p>
        <p className="font-mono text-[11px] leading-relaxed text-zinc-300">{s.body}</p>
        <button data-testid="tour-next" onClick={() => (step + 1 < STEPS.length ? setStep(step + 1) : onDone())}
          className="w-fit px-6 py-2.5 font-bold uppercase tracking-[0.25em] text-xs text-black transition-transform hover:scale-[1.02]"
          style={{ backgroundColor: accent }}>
          {step + 1 < STEPS.length ? "Next" : "Enter the menu"}
        </button>
      </div>
    </div>
  );
}

/* What's New rail — admin-published update bulletins + the returning-player
   welcome-back digest (7+ days away → everything they missed). Reads the
   /nc/menu/world aggregate; no extra fetches. */
export function NewsPanel({ world, accent = "#2E6BFF" }) {
  const { t } = useI18n();
  const wb = world?.me?.welcome_back;
  const bulletins = world?.bulletins || [];
  return (
    <div data-testid="news-panel" className="flex flex-col gap-3">
      {wb && (
        <div data-testid="welcome-back" className="border p-4" style={{ borderColor: `${accent}66`, backgroundColor: `${accent}0f` }}>
          <p className="font-mono text-[10px] font-bold tracking-[0.3em] uppercase flex items-center gap-2" style={{ color: accent }}>
            <PartyPopper className="w-3.5 h-3.5" /> {t("news.welcome_back")}
          </p>
          <p className="font-mono text-[10px] text-zinc-300 mt-2 leading-relaxed">{t("news.away", { n: wb.away_days })}</p>
        </div>
      )}
      {bulletins.length === 0 && (
        <p data-testid="news-empty" className="font-mono text-[10px] text-zinc-500 leading-relaxed border border-dashed border-white/10 p-4">{t("news.empty")}</p>
      )}
      {bulletins.map((b) => (
        <div key={b.id} data-testid="bulletin-card" className="border border-white/10 bg-black/40 p-4">
          <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500">
            {new Date(b.published_at).toLocaleDateString()} · {b.published_by}
          </p>
          <p className="font-sans font-bold text-sm text-zinc-50 mt-1 uppercase tracking-wide">{b.title}</p>
          <p className="font-mono text-[10px] text-zinc-400 mt-1.5 leading-relaxed">{b.body}</p>
          {(b.points || []).length > 0 && (
            <ul className="mt-2.5 flex flex-col gap-1">
              {b.points.map((p, i) => (
                <li key={i} className="font-mono text-[9px] text-zinc-300 flex items-start gap-1.5">
                  <span style={{ color: accent }}>▸</span>{p}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}
